import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import authService from '../services/authService'

// Get user from localStorage
const user = JSON.parse(localStorage.getItem('user'))
const token = localStorage.getItem('token')

const initialState = {
  user: user || null,
  token: token || null,
  isAuthenticated: !!token,
  isLoading: false,
  error: null,
  // Two-factor challenge state (master plan sections 3 & 4.12).
  // `state` is null when no challenge is in flight; otherwise it is
  // 'setup_required' or 'code_required'. `token` is the opaque
  // 64-hex challenge token; `expiresIn` is seconds until it expires.
  twoFactorChallenge: null,
  // One-time display of recovery codes after a first-time enrolment
  // verification succeeds. Cleared when the user acknowledges they
  // have saved them.
  recoveryCodes: null,
}

// Login
export const login = createAsyncThunk('auth/login', async (credentials, thunkAPI) => {
  try {
    return await authService.login(credentials)
  } catch (error) {
    const message = error.response?.data?.message || error.message
    return thunkAPI.rejectWithValue(message)
  }
})

// Two-factor setup: fetch qr / secret / otpauth URI for the current challenge.
export const setupTwoFactor = createAsyncThunk(
  'auth/2fa/setup',
  async (challengeToken, thunkAPI) => {
    try {
      return await authService.setupTwoFactor(challengeToken)
    } catch (error) {
      const message = error.response?.data?.message || error.message
      return thunkAPI.rejectWithValue({
        message,
        status: error.response?.status ?? null,
      })
    }
  },
)

// Two-factor verify: submit a 6-digit code for the current challenge.
// Success mints a JWT and returns the user; a first-time setup verify
// also returns `recovery_codes` to display exactly once.
export const verifyTwoFactor = createAsyncThunk(
  'auth/2fa/verify',
  async ({ challengeToken, code }, thunkAPI) => {
    try {
      return await authService.verifyTwoFactor(challengeToken, code)
    } catch (error) {
      const message = error.response?.data?.message || error.message
      return thunkAPI.rejectWithValue({
        message,
        status: error.response?.status ?? null,
        attemptsRemaining: error.response?.data?.attempts_remaining ?? null,
      })
    }
  },
)

// Two-factor recovery: burn a recovery code against a verify challenge.
// Named `submit...` rather than `use...` so it does not collide with the
// react-hooks/rules-of-hooks linter, which flags any identifier prefixed
// with `use` as a hook when called outside a component body.
export const submitRecoveryCode = createAsyncThunk(
  'auth/2fa/recovery',
  async ({ challengeToken, recoveryCode }, thunkAPI) => {
    try {
      return await authService.useRecoveryCode(challengeToken, recoveryCode)
    } catch (error) {
      const message = error.response?.data?.message || error.message
      return thunkAPI.rejectWithValue({
        message,
        status: error.response?.status ?? null,
        attemptsRemaining: error.response?.data?.attempts_remaining ?? null,
      })
    }
  },
)

// Logout
export const logout = createAsyncThunk('auth/logout', async () => {
  try {
    await authService.logout()
  } catch (error) {
    // Continue with logout even if API fails
  }
})

// Get current user
export const getCurrentUser = createAsyncThunk('auth/me', async (_, thunkAPI) => {
  try {
    return await authService.getCurrentUser()
  } catch (error) {
    const message = error.response?.data?.message || error.message
    return thunkAPI.rejectWithValue(message)
  }
})

// Apply a successful login/verify/recovery payload. Isolates the
// "isAuthenticated only when access_token is present" invariant so
// the three success paths cannot drift.
const applyAuthenticatedPayload = (state, payload) => {
  state.isLoading = false
  state.isAuthenticated = true
  state.user = payload.user
  state.token = payload.access_token
  state.twoFactorChallenge = null
  state.error = null
  if (payload.recovery_codes) {
    state.recoveryCodes = payload.recovery_codes
  }
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false
      state.error = null
    },
    clearError: (state) => {
      state.error = null
    },
    clearTwoFactorChallenge: (state) => {
      state.twoFactorChallenge = null
      state.error = null
    },
    acknowledgeRecoveryCodes: (state) => {
      state.recoveryCodes = null
    },
    tokenRefreshed: (state, action) => {
      state.token = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false
        // Discriminator (plan section 3): `access_token` present ⇒
        // logged in; `two_factor` present ⇒ challenge issued. Never
        // set isAuthenticated on a challenge response — that was the
        // half-authenticated dashboard flash the plan warned about.
        if (action.payload.access_token) {
          applyAuthenticatedPayload(state, action.payload)
        } else if (action.payload.two_factor) {
          state.twoFactorChallenge = {
            state: action.payload.two_factor,
            token: action.payload.challenge_token,
            expiresIn: action.payload.expires_in,
          }
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
        state.user = null
        state.token = null
        state.isAuthenticated = false
        state.twoFactorChallenge = null
      })
      // 2FA setup
      .addCase(setupTwoFactor.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(setupTwoFactor.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(setupTwoFactor.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload?.message ?? 'Setup failed.'
        // A 410 means the challenge is gone; drop the client-side
        // challenge so the user is bounced back to the password step
        // rather than being stuck on a dead flow.
        if (action.payload?.status === 410) {
          state.twoFactorChallenge = null
        }
      })
      // 2FA verify
      .addCase(verifyTwoFactor.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(verifyTwoFactor.fulfilled, (state, action) => {
        applyAuthenticatedPayload(state, action.payload)
      })
      .addCase(verifyTwoFactor.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload?.message ?? 'Verification failed.'
        if (action.payload?.status === 410) {
          state.twoFactorChallenge = null
        }
      })
      // 2FA recovery code
      .addCase(submitRecoveryCode.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(submitRecoveryCode.fulfilled, (state, action) => {
        applyAuthenticatedPayload(state, action.payload)
      })
      .addCase(submitRecoveryCode.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload?.message ?? 'Recovery code failed.'
        if (action.payload?.status === 410) {
          state.twoFactorChallenge = null
        }
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null
        state.token = null
        state.isAuthenticated = false
        state.twoFactorChallenge = null
        state.recoveryCodes = null
      })
      // Get current user
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = true
        state.user = action.payload.data || action.payload.user || action.payload
        state.error = null
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        // Do NOT clear isAuthenticated or user here.
        // The interceptor in api.js calls forceLogout() on definitive auth failures,
        // which clears localStorage and redirects to /login.
        // Clearing auth state here too causes logout on any transient error (network, 500, etc.).
        state.isLoading = false
        state.error = action.payload
      })
  },
})

export const {
  reset,
  clearError,
  clearTwoFactorChallenge,
  acknowledgeRecoveryCodes,
  tokenRefreshed,
} = authSlice.actions
export default authSlice.reducer
